import type { ValueOf } from '@shared/lib'
import type { OlympiadLang } from '@shared/lib/locale'

export const OlympiadType = {
  spring: 'spring',
  summer: 'summer',
  autumn: 'autumn',
  winter: 'winter',
  international: 'international',
} as const
export type OlympiadType = ValueOf<typeof OlympiadType>

export const OlympiadStatus = {
  ongoing: 'ongoing',
  finished: 'finished',
  upcoming: 'upcoming',
} as const
export type OlympiadStatus = ValueOf<typeof OlympiadStatus>

export const OlympiadFormat = {
  online: 'online',
  offline: 'offline',
  hybrid: 'hybrid',
} as const
export type OlympiadFormat = ValueOf<typeof OlympiadFormat>

export const AssignmentFormat = {
  online: 'online',
  offline: 'offline',
  hybrid: 'hybrid',
} as const
export type AssignmentFormat = ValueOf<typeof AssignmentFormat>

/** Фиксированный набор этапов олимпиады (`Stage.type` контракта).
 *
 *  До 2026-08-05 бэк хранил свободную строку `name`, и фронт слал русские
 *  подписи. Теперь это enum — мост «ключ ↔ ru-подпись» больше не нужен.
 *  Значений восемь; макет визарда рисует семь строк (открытый вопрос по паре
 *  `teacher_thank_you` / `teacher_emails` — см. STAGES.md этапа 2). */
export const OlympiadStageType = {
  registration: 'registration',
  olympiadDay: 'olympiad_day',
  results: 'results',
  certificates: 'certificates',
  finalResults: 'final_results',
  diplomas: 'diplomas',
  teacherThankYou: 'teacher_thank_you',
  teacherEmails: 'teacher_emails',
} as const
export type OlympiadStageType = ValueOf<typeof OlympiadStageType>

export interface OlympiadStage {
  id?: number
  /** `null` приходит у этапов, заведённых до перехода на enum: миграция не
   *  смогла угадать тип по старой свободной строке (проверено на стенде
   *  2026-08-06 — олимпиада 88 отдаёт `"type": null`). В исходящем payload
   *  всегда заполнен: строки без типа сабмит отбрасывает. */
  type: OlympiadStageType | null
  start_date: string
  /** Только у «Регистрации» — остальные этапы одна дата. Контракт требует
   *  лишь `start_date`, поэтому одиночную дату больше не дублируем в конец. */
  end_date?: string
}

/** Описание олимпиады на одном языке. Заменило единственное поле
 *  `Olympiad.description` — теперь текст хранится по языку. */
export interface OlympiadTranslation {
  language: OlympiadLang
  description: string
}

/** Класс + количество задач для него. Заменило пару `grades[]` + форменное
 *  `task_counts`, которое до 2026-08-05 вообще не уходило на бэк. */
export interface OlympiadGradeSetting {
  grade: number
  task_count: number
}

/** Окно проведения внутри слота: один класс, одно время.
 *  `start_at`/`end_at` — ISO с offset, напр. `2026-10-07T10:00:00+05:00`. */
export interface OlympiadSlotSchedule {
  id?: number
  grade: number
  start_at: string
  end_at: string
}

/** Слот проведения: страна (+ город и адрес у оффлайна) и его расписание по
 *  классам. Заменил плоский `assignment_slots[]`, где на каждый класс шла
 *  отдельная строка, — теперь классы живут внутри `schedules`.
 *
 *  `country` и `city` — первичные ключи справочников (`LocationCountry.id`,
 *  `LocationCity.id`), а не ISO-код и не название. */
export interface OlympiadFormatSlot {
  id?: number
  format: OlympiadFormat
  country: number
  /** IANA-идентификатор, напр. `Asia/Almaty`. Онлайн-слот проводится по этой
   *  таймзоне независимо от того, где находится участник. */
  timezone?: string
  city?: number
  /** Адрес площадки на трёх языках. Русский обязателен у офлайн-слота (бэк
   *  отвечает 400 без него), остальные приходят пустой строкой. У онлайн-слота
   *  адресных полей нет. */
  address_ru?: string
  address_kk?: string
  address_en?: string
  schedules: OlympiadSlotSchedule[]
}

/** Этап в том виде, в каком его отдаёт участнику `/olympiads/participant/…`:
 *  свободная строка-подпись вместо `type`. См. {@link ParticipantOlympiad}. */
export interface ParticipantOlympiadStage {
  /** Появился в ответе участника 2026-08-09 — до этого этап приезжал только
   *  свободной строкой `name` по-русски. У легаси-олимпиад (та же 86) он
   *  по-прежнему `null`, и подписать этап тогда нечем, кроме `name`. */
  type?: OlympiadStageType | null
  name: string
  start_date: string
  end_date: string
}

/** Слот в том виде, в каком его отдаёт участнику `/olympiads/participant/…`.
 *
 *  Админский контракт переехал на {@link OlympiadFormatSlot}, участнический —
 *  ещё нет, поэтому плоская форма жива и используется страницами участника. */
export interface AssignmentSlot {
  grade: number
  format: AssignmentFormat
  start_at: string
  end_at: string
  city?: string
  address?: string
}

// Bek stores one row per award category. Each row carries the type and a
// background image URL (or File on input). Olympiad.certificate_templates is
// the full collection — the singular certificate_template field was removed.
export const CertificateTemplateType = {
  certificate: 'certificate',
  diploma_1: 'diploma_1',
  diploma_2: 'diploma_2',
  diploma_3: 'diploma_3',
  honorable: 'honorable',
} as const
export type CertificateTemplateType = ValueOf<typeof CertificateTemplateType>

export interface CertificateTemplate {
  id?: number
  type: CertificateTemplateType
  background?: string
}

export interface CertificateTemplateInput {
  type: CertificateTemplateType
  /** File for upload, or undefined to leave the existing template untouched.
   *  To drop a template, omit the whole object from the array. */
  background?: File
}

/** Строка списка `/olympiads/dashboard/` (`AdministratorOlympiadList`).
 *
 *  Это НЕ усечённая {@link Olympiad}: список отдаёт `grades[]` плоским
 *  массивом, которого у детали нет (там `grade_settings`), и не отдаёт ни
 *  формата, ни языков. Поэтому отдельный тип, а не `Pick<Olympiad, …>`. */
export interface OlympiadListItem {
  id: number
  title: string
  type: OlympiadType
  grades: number[]
  year: number
  participant_count: number
  status: OlympiadStatus
}

/** Олимпиада целиком — ответ `GET /olympiads/dashboard/{id}/`.
 *
 *  Не `extends OlympiadPayload`: у ответа шаблоны приходят ссылками
 *  (`background: string`), а в запрос уходят файлами (`background: File`). */
export interface Olympiad {
  id: number
  title: string
  type: OlympiadType
  languages: OlympiadLang[]
  cost: string
  translations: OlympiadTranslation[]
  grade_settings: OlympiadGradeSetting[]
  stages: OlympiadStage[]
  format: OlympiadFormat
  format_slots: OlympiadFormatSlot[]
  proctoring?: boolean
  certificate_templates?: CertificateTemplate[]
  /** Считаются бэком (`readOnly` в контракте) — отправлять их нельзя. */
  year: number
  status: OlympiadStatus
  created_at?: string
}

/** Записываемая часть олимпиады — тело `POST` и `PUT`/`PATCH`.
 *
 *  Уходит всегда как `multipart/form-data`: вложенные разделы — JSON-строками,
 *  файлы шаблонов — отдельными ключами (см. `buildOlympiadFormData`). */
export interface OlympiadPayload {
  title: string
  type: OlympiadType
  languages: OlympiadLang[]
  /** Строка-decimal по контракту, не число. */
  cost: string
  translations: OlympiadTranslation[]
  grade_settings: OlympiadGradeSetting[]
  stages: OlympiadStage[]
  format: OlympiadFormat
  format_slots: OlympiadFormatSlot[]
  /** Только у форматов с онлайн-блоком; для чистого оффлайна бэк требует `false`. */
  proctoring?: boolean
  certificate_templates?: CertificateTemplateInput[]
}

export interface CommonOlympiad {
  id: number
  title: string
  year?: number
}

/** Состояние участия, каким его видит бэк. Оно и решает, что показывать:
 *  выводить состояние из `status` и дат на фронте больше не нужно. */
export const ParticipantTimingState = {
  registrationNotStarted: 'registration_not_started',
  registrationOpen: 'registration_open',
  registrationClosed: 'registration_closed',
  registered: 'registered',
  inProgress: 'in_progress',
  finished: 'finished',
  disqualified: 'disqualified',
} as const
export type ParticipantTimingState = ValueOf<typeof ParticipantTimingState>

/** Причина, по которой участие недоступно. Коды стабильны — на них и
 *  завязаны сообщения; `detail` бэк локализует по `Accept-Language`. */
export interface ParticipantTimingError {
  code: string
  detail: string
}

/** Расписание участника и права на действия.
 *
 *  ⚠ Три формы ответа, все встречены живьём 2026-08-08: полная (олимпиада
 *  127), отказ `{available: false, error}` (86) и `null` целиком (101).
 *  Поэтому поле необязательное, а `available` разделяет первые две.
 *
 *  Флаги `can_*` — **единственный** источник правды о том, можно ли
 *  регистрироваться, править регистрацию и стартовать (требование бэка от
 *  2026-08-08). Часы браузера годятся только на отрисовку. */
export interface ParticipantTiming {
  available: boolean
  error?: ParticipantTimingError
  /** IANA-зона слота, на который участника определили по стране и классу. */
  timezone?: string
  /** ISO со смещением. Парсить как есть — вычитать зоны руками нельзя. */
  start_at?: string
  end_at?: string
  registration_starts_at?: string
  registration_ends_at?: string
  /** `start_at` минус 7 дней — после него язык и фото не поменять. */
  edit_deadline?: string
  /** Время бэка на момент ответа: от него считается отсчёт, чтобы сбитые
   *  часы устройства не открывали и не закрывали кнопки. */
  server_now?: string
  can_register?: boolean
  can_edit_registration?: boolean
  /** Вход в прокторинг открывается за 15 минут до старта — раньше, чем сам
   *  старт. Поэтому `can_onboard: true` при `can_start: false` — нормальное
   *  состояние, а не рассинхрон: участник уже может заходить, олимпиада ещё
   *  не началась (контракт PROCTOR от 2026-09-16, §2). */
  can_onboard?: boolean
  can_start?: boolean
  state?: ParticipantTimingState
  /** Маршрут, разрешённый этому участнику. В списке олимпиад это единственное
   *  место, где он есть: верхнеуровневого `participation_format` там нет
   *  (проба стенда 2026-08-26). */
  participation_format?: OlympiadFormat | null
}

/** Регистрация участника. `null` означает, что её нет вовсе: отдельного
 *  состояния «оплачено, но не зарегистрирован» не существует — бэк создаёт
 *  списание и регистрацию одной транзакцией. */
/**
 * Площадка, на которую участник МОЖЕТ записаться, — решает бэк.
 *
 * Это не то же, что `assignment_slots`: там лежит всё расписание олимпиады по
 * классам, включая чужие города, и фронт раньше фильтровал его сам. Здесь
 * список уже сведён под класс и локацию участника, а у каждой строки есть
 * собственный `can_register` — площадка с ближайшей датой закрывается раньше
 * остальных (проверено на стенде 2026-08-21: регистрация на такую отвечает
 * `registration_closed`).
 */
export interface EligibleVenue {
  id: number
  country: { id: number; code: string }
  city: { id: number; name: string }
  address: string
  timezone: string
  start_at: string
  end_at: string
  can_register: boolean
}

/**
 * Площадка в снимке регистрации — форма ПЛОСКАЯ и другая, чем в
 * `eligible_venues` (проверено на стенде 2026-08-21): здесь нет ни времени, ни
 * зоны, только адрес. Время и зону выбранной площадки бэк уже подставил в
 * `participant_timing.start_at` / `timezone`, сверять их со списком не нужно.
 */
export interface RegisteredVenue {
  id: number
  country_code: string
  city: string
  address: string
}

export interface ParticipantRegistration {
  id: number
  status: OlympiadParticipantStatus
  /** Код из справочника причин, у старых дисквалификаций — свободный текст,
   *  у активной регистрации `null`. Подпись — `registrationReasonLabel`. */
  reason?: string | null
  language: string | null
  photo: string | null
  registered_at?: string
  /** Маршрут, по которому участник записан. У гибрида отличает офлайн от онлайна. */
  participation_format?: OlympiadFormat | null
  /** Выбранная площадка — только у офлайн- и гибрид-офлайн регистраций. */
  venue?: RegisteredVenue | null
}

export interface ParticipantOlympiad {
  id: number
  title: string
  description: string | null
  status: string
  registered: boolean
  tour_type: string | null
  first_start_date: string | null
  last_end_date: string | null
  time_left: number | null
  price: number | null
  currency: string | null
  registration_status: string | null
  /** Легаси-адрес сайта олимпиады. **Не ссылка запуска и не пробный
   *  прокторинг** (контракт PROCTOR от 2026-09-16 §2): фронт его больше не
   *  читает, поле оставлено описанием ответа бэка. */
  url: string | null
  // Ниже — поля, которые бэк уже отдаёт в этом же ответе (проверено на staging
  // 2026-08-04), но которыми до детальной страницы участника никто не пользовался.
  format: OlympiadFormat | null
  location: string | null
  year: number
  language: string | null
  start_at: string | null
  /** Языки олимпиады. Выбор при регистрации ограничен ими же. */
  languages?: OlympiadLang[]
  /** Только в детальном ответе. */
  proctoring?: boolean
  /** Число задач для класса участника. Появилось в ответе 2026-08-09 — до
   *  этого плитку «Кол.во задач» из макета нечем было заполнить. */
  task_count?: number | null
  participant_timing?: ParticipantTiming | null
  /** То же состояние, что в `participant_timing.state`, отдельным полем —
   *  приходит и там, где тайминга нет целиком (проверено на стенде
   *  2026-08-22). `null` — участник этой олимпиады не касался. */
  participant_state?: ParticipantTimingState | null
  participant_registration?: ParticipantRegistration | null
  /** Площадки под класс и локацию участника. Пусто у онлайн-олимпиад. */
  eligible_venues?: EligibleVenue[]
  /** Нужен ли явный выбор. Считает бэк — своей формулой «площадок больше
   *  одной» пользоваться нельзя: недоступные строки в списке тоже есть. */
  venue_selection_required?: boolean
  /** Маршрут, который сервер разрешил этому участнику. У гибрида решает он. */
  participation_format?: OlympiadFormat | null
  /** Класс участника, под который сведено расписание. */
  participant_grade?: number | null
  // Этапы участнику приходят в легаси-форме — со свободной строкой `name`
  // вместо enum `type` (проверено 2026-08-08: у олимпиады 86 `type: null`,
  // `name: "Регистрация"`). Админский ответ поле `name` уже потерял.
  stages?: ParticipantOlympiadStage[]
  assignment_slots?: AssignmentSlot[]
}

/** Состояние регистрации участника в олимпиаде. Админский фильтр принимает
 *  только `active` и `disqualified` (бэк отвечает «status must be active or
 *  disqualified»); `cancelled` приходит участнику в `participant_registration`. */
export const OlympiadParticipantStatus = {
  active: 'active',
  cancelled: 'cancelled',
  disqualified: 'disqualified',
} as const
export type OlympiadParticipantStatus = ValueOf<typeof OlympiadParticipantStatus>

/** Страна/город в строке регистрации приходят объектом, а не кодом или id, —
 *  в отличие от остальных списков админки. */
export interface OlympiadParticipantCountry {
  id: number
  code: string
}

export interface OlympiadParticipantCity {
  id: number
  name: string
}

/**
 * Строка списка участников олимпиады — это **регистрация**, не профиль.
 *
 * Контракт снят пробами стенда 2026-08-08: сгенерированный `openapi.json`
 * описывает этот путь заглушкой со ссылкой на `Olympiad` и полей не содержит.
 */
export interface OlympiadParticipantRow {
  /** Идентификатор регистрации: именно он уходит в смену времени и
   *  дисквалификацию. С `participant_id` не совпадает. */
  id: number
  /** Ключ таблицы участников — в интерфейсе нигде не показывается: с номером
   *  в «Пользователях» и в кабинете он не совпадает. */
  participant_id: number
  /** Номер участника, который видит админ в «Пользователях» и сам участник в
   *  кабинете, — его и показывает колонка «ID»; по нему же она сортируется
   *  (`ordering=profile_id`). */
  profile_id: number
  full_name: string
  country: OlympiadParticipantCountry | null
  city: OlympiadParticipantCity | null
  grade: number | null
  /** IANA-идентификатор либо пустая строка, если время ещё не назначено. */
  timezone: string
  /** Смещение зоны **строкой ISO** (`"+05:00"`), а не числом минут — проверено
   *  живым ответом стенда 2026-08-08. `null`, пока время не назначено. */
  utc_offset: string | null
  start_at: string | null
  end_at: string | null
  status: OlympiadParticipantStatus
}

/** Город в справочнике фильтров несёт свою страну — список городов сужается
 *  по выбранной стране без отдельного запроса. */
export interface OlympiadParticipantFilterCity extends OlympiadParticipantCity {
  country_id: number
}

/** Справочники для фильтров — только то, что реально представлено
 *  регистрациями этой олимпиады. */
export interface OlympiadParticipantFilters {
  countries: OlympiadParticipantCountry[]
  cities: OlympiadParticipantFilterCity[]
  grades: number[]
}

/** Ответ на дисквалификацию (проверено живым вызовом 2026-08-08). Бэк
 *  сообщает сумму, которая вернулась участнику, — админу это важнее самого
 *  факта: действие необратимо и денежное. */
export interface OlympiadParticipantDisqualifyResult {
  id: number
  status: OlympiadParticipantStatus
  disqualified_at: string
  refunded_amount: string
  currency: string
}

/** Тело массовой смены времени. Даты — полные, со смещением: календарный день
 *  берётся из этапа «День олимпиады», как и у слотов визарда. */
export interface OlympiadParticipantTimePayload {
  registration_ids: number[]
  timezone: string
  start_at: string
  end_at: string
}
